import { Component, Inject, OnDestroy } from '@angular/core';
import { DwComponent, DwComponentType, DwEventService, DwExpressionService, DwMessage, DwMessageSeverity, DwMetaDataFormApi, DwMetaDataFormStateService, DwMetaDataService, DwMetaDataServiceToken, DwOrmDataService, DwOrmDataServiceToken, DwSectionBaseComponent, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken } from '@devwareapps/devware-cap';
import { SchoolRepositoryService } from 'src/app/features/schools/services/school-repository.service';
import { AppMetaDataItemNames, AssignmentEntity, VideoEntity } from 'src/app/meta-data/app-meta-data.service';
import { VideoEvent } from '../../models/video-event.model';
import { VideoEventType } from '../../models/video-event-type.enum';
import { VideoUploadService } from '../../services/video-upload.service';
import { VideoModalService } from '../../services/video-modal.service';
import { VideoRepositoryService } from '../../services/video-repository.service';
import { Subscription } from 'rxjs';


@DwComponent({
  key: 'videoThumbnailPreview',
  name: 'Video Thumbnail Preview',
  componentType: DwComponentType.formSection,
  isGlobal: true,
  parentItemName: AppMetaDataItemNames.Video,
})
@Component({
  selector: 'app-video-thumbnail',
  template: `
    <img *ngIf="thumbnailUrl" [src]="thumbnailUrl" [alt]="videoEntity?.VideoName || 'Video thumbnail'"
      style="width:100%;height:auto;max-width:1000px">
  `
})
export class VideoThumbnailComponent extends DwSectionBaseComponent implements OnDestroy {
  videoEntity: VideoEntity;
  thumbnailUrl: string;

  originalAssignment: AssignmentEntity;
  formApi: DwMetaDataFormApi;
  lastTime: number = null;
  private previewSubscription: Subscription;
  private previewSignature: string;

  constructor(
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private dataService: DwOrmDataService,
    private dwFormStateService: DwMetaDataFormStateService,
    private videoUploadService: VideoUploadService,
    private videoModalService: VideoModalService,
    private videoRepositoryService: VideoRepositoryService

  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {

    this.setVideoEntity(this.formApi.getFormData<VideoEntity>());

    this.formApi.getFormDataChanges<VideoEntity>()
      .subscribe(videoEntity => {
        this.setVideoEntity(videoEntity);
      });

  }

  ngOnDestroy(): void {
    this.previewSubscription?.unsubscribe();
  }


  videoEvent(videoEvent: VideoEvent) {

    switch (videoEvent.videoEventType) {
      case VideoEventType.videoStopped:
      case VideoEventType.videoPaused:
        this.lastTime = videoEvent.videoTime;

        break;
    }
  }

  message: DwMessage;

  setThumbnailTime() {
    if (this.lastTime == null) {
      return;
    }

    this.videoUploadService.setThumnbailTime(this.videoEntity.VideoId, this.lastTime)
      .subscribe(result => {
        if (result.IsSuccess) {
          this.message = {
            messageBody: 'Thumbnail set successful.  It might take several seconds for the thumbnail to be updated.  Use the refresh button to see the updated thumbail.',
            severity: DwMessageSeverity.success,
            allowClose: true
          };

          return;
        }

        this.message = {
          messageBody: `Thumbnail set failed. Error : ${result.ErrorMessage}`,
          severity: DwMessageSeverity.error,
          allowClose: true,
        }
      });
  }

  replaceVideoModal() {
    this.videoModalService.showVideoUploadModal('Replace Video', this.videoEntity)
      .subscribe(result => {
        this.formApi.cancel();
      });
  }

  refreshVideo() {
    this.loadPreview(true);
  }

  close() {
    this.formApi.cancel();
  }

  private setVideoEntity(videoEntity: VideoEntity): void {
    this.videoEntity = videoEntity;
    this.loadPreview();
  }

  private loadPreview(forceRefresh: boolean = false): void {
    const signature = this.getPreviewSignature(this.videoEntity);
    if (!forceRefresh && signature === this.previewSignature) {
      return;
    }

    this.previewSignature = signature;
    this.thumbnailUrl = null;
    this.previewSubscription?.unsubscribe();

    if (!this.videoEntity?.VideoId) {
      return;
    }

    const videoId = this.videoEntity.VideoId;
    this.previewSubscription = this.videoRepositoryService.getVideoPreviewPlayback(videoId)
      .subscribe(
        playback => {
          if (this.videoEntity?.VideoId === videoId) {
            this.thumbnailUrl = playback.ThumbnailUrl;
          }
        },
        () => {
          if (this.videoEntity?.VideoId === videoId) {
            this.thumbnailUrl = null;
          }
        });
  }

  private getPreviewSignature(videoEntity: VideoEntity): string {
    if (!videoEntity?.VideoId) {
      return null;
    }

    return [
      videoEntity.VideoId,
      videoEntity.VideoLibraryId,
      videoEntity.VideoStreamingProviderId,
      videoEntity.VideoKey,
      videoEntity.ThumbnailUrl,
      videoEntity.ModifiedDateTime
    ].join('|');
  }

}
